import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import { toast } from 'react-toastify';
import { getVisitStatusBadgeClass, mapAdminVisitFromApi } from '../../Utils/adminVisits';

function AdminHome() {
  const [summary, setSummary] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, visitsRes] = await Promise.all([
        client.get('/api/admin/dashboard'),
        client.get('/api/admin/visit-requests', { params: { status: 'all' } }),
      ]);

      if (summaryRes.data?.success) {
        setSummary(summaryRes.data.data);
      }

      if (visitsRes.data?.success && Array.isArray(visitsRes.data.data)) {
        setRecentVisits(
          visitsRes.data.data.slice(0, 5).map(mapAdminVisitFromApi),
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatRevenue = (value) => {
    const n = Number(value) || 0;
    return `₹${n.toLocaleString('en-IN')}`;
  };

  return (
    <>
      <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
        <h2 className="dashboard-title mb-0">Dashboard</h2>
        <Link to="/admin/visit-requests" className="btn btn-outline-primary btn-sm">
          View All Visit Requests
        </Link>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-muted small">Total Patients</div>
              <div className="fs-3 fw-bold text-primary">
                {loading ? '—' : summary?.totalPatients ?? 0}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-muted small">Pending Requests</div>
              <div className="fs-3 fw-bold text-warning">
                {loading ? '—' : summary?.pendingRequests ?? 0}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-muted small">Completed (Month)</div>
              <div className="fs-3 fw-bold text-success">
                {loading ? '—' : summary?.completedVisits ?? 0}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-muted small">Active Doctors</div>
              <div className="fs-3 fw-bold" style={{ color: '#124189' }}>
                {loading ? '—' : summary?.activeDoctors ?? 0}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-muted small">Revenue (Month)</div>
              <div className="fs-4 fw-bold text-success">
                {loading ? '—' : formatRevenue(summary?.revenueThisMonth)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Recent Visit Requests</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Patient</th>
                  <th>Service</th>
                  <th>Pref. Date</th>
                  <th>Status</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && recentVisits.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No visit requests yet.
                    </td>
                  </tr>
                )}
                {!loading &&
                  recentVisits.map((v) => (
                    <tr key={v.id}>
                      <td className="fw-bold">
                        <Link to="/admin/visit-requests" className="text-primary text-decoration-none">
                          {v.patientName}
                        </Link>
                      </td>
                      <td>{v.serviceTypeName}</td>
                      <td>{v.preferredDate}</td>
                      <td>
                        <span className={getVisitStatusBadgeClass(v.status)}>{v.statusLabel}</span>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{v.paymentLabel}</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminHome;
